import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Search } from '@shared/components/ui/Search';
import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { cn } from '@shared/utils/utils';
import { ColumnDef } from '@tanstack/react-table';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Withdrawal } from '../../models';
import { WithdrawalsFiltersPanel } from './WithdrawalsFiltersPanel';

interface FiltersSectionProps {
  columns: ColumnDef<Withdrawal>[];
  selectedColumns: string[];
  fixedColumns?: string[];
  onColumnsChange: (selected: string[]) => void;
  searchValue: string;
  onSearch: (value: string) => void;
  filterConditions: FilterConditionOutput[];
  onFilterChange: (conditions: FilterConditionOutput[]) => void;
  className?: string;
}

export const FiltersSection = ({
  columns,
  selectedColumns,
  fixedColumns = [],
  onColumnsChange,
  searchValue,
  onSearch,
  filterConditions,
  onFilterChange,
  className,
}: FiltersSectionProps) => {
  const { t } = useTranslation('withdrawalsModule');
  const { t: tUI } = useTranslation('ui');

  const [inputValue, setInputValue] = useState(searchValue);

  useEffect(() => {
    setInputValue((prev) => (prev === searchValue ? prev : searchValue));
  }, [searchValue]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleSearchSubmit = useCallback(() => {
    onSearch(inputValue.trim());
  }, [inputValue, onSearch]);

  const activeFiltersCount = filterConditions.length;

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      <Search
        placeholder={t('search.placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="withdrawals-search"
        inputDataTestId="withdrawals-search-input"
      />

      <FiltersDropdown
        title={tUI('advancedFilter.filters', 'Filtros')}
        activeFiltersCount={activeFiltersCount}
        dropdownContentClassName="sm:min-w-[350px] sm:max-w-[400px] p-4 bg-body-bg text-foreground border border-card-border"
      >
        <WithdrawalsFiltersPanel initialConditions={filterConditions} onChange={onFilterChange} />
      </FiltersDropdown>

      <ColumnSelector
        columns={columns}
        selected={selectedColumns}
        fixedColumns={fixedColumns}
        onColumnsChange={onColumnsChange}
        dataTestId="withdrawals-column-selector"
        storageKey="withdrawals-ag-grid-table"
      />
    </div>
  );
};
